import React from 'react';
import { Tooltip } from 'antd';
import { EBenefitPeriod, TAccumulatorTile, TCostSharing, TVisitCostTile } from 'types/insuranceEligibility';
import { formatCents, priceFirst } from '../derive';
import { TILE_COPY, TILE_LABELS, scopeLine } from '../copy';
import s from '../s.module.less';

type TileData = {
    label: string;
    main: string;
    sub?: string;
    // sub 被 CSS 截到两行时的完整内容，挂 antd Tooltip。只有 payer 附言会长到需要它——
    // 前端自己的默认副行都是两三个词，不挂
    subFull?: string;
    scope?: string;
    na?: boolean;
};

const visitTile = (label: string, t: TVisitCostTile, isCoinsurance: boolean): TileData => {
    if (!t.reported) {
        return { label, main: TILE_COPY.notReported, sub: TILE_COPY.notReportedSub, na: true };
    }
    const values = t.values || [];
    const fmt = (v: number) => (t.kind === 'PERCENT' ? `${v}%` : formatCents(v));
    if (values.length > 1) {
        // payer 自相矛盾：两个都印，不替 payer 选（"$30 or $60 · payer sent both"）
        return { label, main: values.map(fmt).join(' or '), sub: TILE_COPY.conflictSub, na: true };
    }
    // payer 没说时的默认副行：copay 恒 "per visit"、coinsurance 恒 "after deductible"
    // （原型两个 tile 都不留白）。但 scope 行由后端 period 渲染，VISIT 就是 "Per visit"——
    // 两行连着出现是同一句话的两种大小写。让路的是前端补的默认值，不是后端下发的事实
    const scopeSaysPerVisit = t.scope?.period === EBenefitPeriod.VISIT;
    const fallback = isCoinsurance ? TILE_COPY.afterDeductible : TILE_COPY.perVisit;
    return {
        label,
        main: fmt(values[0] ?? 0),
        // payer 附言（271 备注）优先
        sub: t.note || (!isCoinsurance && scopeSaysPerVisit ? undefined : fallback),
        subFull: t.note || undefined,
        scope: scopeLine(t.scope),
    };
};

// flip = Deductible 专属的剩余额领字形态（"$420 / left of $1,500"——今天还要花多少才是 provider
// 关心的数）；OOP max 是年终封顶线，维持「总额领字 / $X remaining 副行」（原型 tileDeductible vs tile）。
// 补算出的腿标 "(calculated)"，跟着 sub 行走（原型如此，含 flip 形态）
const accumulatorTile = (label: string, t: TAccumulatorTile, flip: boolean): TileData => {
    if (!t.reported || t.totalCents == null) {
        return { label, main: TILE_COPY.notReported, sub: TILE_COPY.notReportedSub, na: true };
    }
    const total = formatCents(t.totalCents);
    let main = total;
    let sub: string | undefined;
    if (flip && t.remainingCents != null) {
        main = formatCents(t.remainingCents);
        sub = TILE_COPY.leftOf(total);
    } else if (t.remainingCents != null) {
        sub = TILE_COPY.remainingSub(formatCents(t.remainingCents));
    } else if (t.metCents != null) {
        sub = TILE_COPY.metSub(formatCents(t.metCents));
    }
    if (t.derived && sub) {
        sub = `${sub} ${TILE_COPY.calculated}`;
    }
    return { label, main, sub, scope: scopeLine(t.scope) };
};

type Props = {
    costSharing: TCostSharing;
};

// 副行：payer 附言会被 CSS 截到两行，全文走 antd Tooltip（原生 title 在这个格子里不出提示）。
// 前端自己补的默认副行没有 subFull，不包 Tooltip——短短两三个词再悬浮重复一遍是噪音
const renderSub = (t: TileData): React.ReactNode => {
    if (!t.sub) return null;
    const line = <div className={s.tileSub}>{t.sub}</div>;
    return t.subFull ? (
        <Tooltip
            title={t.subFull}
            // 白底：附言是 payer 原文（常见全大写的长句），深底白字读一段大写文本很吃力。
            // color 同时决定箭头颜色，文字色要跟着翻过来，否则是白底白字
            color="#fff"
            overlayStyle={{ maxWidth: 280 }}
            overlayInnerStyle={{ color: '#374151', fontSize: 12, lineHeight: '17px' }}
        >
            {line}
        </Tooltip>
    ) : (
        line
    );
};

// 两列编排：左列单次费用（copay 类 + coinsurance），右列累计额度（deductible / OOP max）。
// 原来两边各两格正好 2×2；medical copay 让左列变三格，右列空出最后一格，列的语义不变。
// medical 只在 payer 给了一般医疗门诊那一行（STC=98）时成格——没有这个数时多一格斜纹
// "Not reported" 只是噪音，卡面退回原来的 2×2
const CostTiles: React.FC<Props> = ({ costSharing }) => {
    const hasMedical = costSharing.medicalCopay.reported;
    const copay = visitTile(TILE_LABELS.copay, costSharing.copay, false);
    const coinsurance = visitTile(TILE_LABELS.coinsurance, costSharing.coinsurance, true);
    // medical 紧跟 BH：两个 copay 中间插进 coinsurance 会让人以为它们分属不同的问题
    const copayTiles = hasMedical
        ? [copay, visitTile(TILE_LABELS.copayMedical, costSharing.medicalCopay, false)]
        : [copay];
    const prices = priceFirst(costSharing) ? [...copayTiles, coinsurance] : [coinsurance, ...copayTiles];
    const accumulators = [
        accumulatorTile(TILE_LABELS.deductible, costSharing.deductible, true),
        accumulatorTile(TILE_LABELS.oopMax, costSharing.oopMax, false),
    ];
    const tiles: TileData[] = [];
    for (let i = 0; i < Math.max(prices.length, accumulators.length); i += 1) {
        if (prices[i]) tiles.push(prices[i]);
        if (accumulators[i]) tiles.push(accumulators[i]);
    }
    return (
        <div className={s.tiles}>
            {tiles.map((t) => (
                <div key={t.label} className={`${s.tile} ${t.na ? s.tileNa : ''}`}>
                    <div className={s.tileLabel}>{t.label}</div>
                    <div className={s.tileMain}>{t.main}</div>
                    {renderSub(t)}
                    {t.scope && <div className={s.tileScope}>{t.scope}</div>}
                </div>
            ))}
        </div>
    );
};

export default CostTiles;
